import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { FileText, Plus, Send, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import OfferTemplateBar from './OfferTemplateBar';

const EMPTY_ITEM = { description: '', quantity: 1, unit_price: '' };

// Eigenständiges Angebot (ohne Reservierung): Gastdaten + Positionen erfassen,
// als Entwurf speichern oder per E-Mail senden (PDF + Annehmen-Link inklusive).
export default function StandaloneOfferDialog({ open, onClose, offerId, onSaved }) {
  const [form, setForm] = useState({ customer_name: '', email: '', phone: '', event_date: '', event_time: '', persons: '' });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!offerId) {
      setForm({ customer_name: '', email: '', phone: '', event_date: '', event_time: '', persons: '' });
      setItems([{ ...EMPTY_ITEM }]);
      setNote('');
      return;
    }
    setLoading(true);
    api.get(`/standalone-offers/${offerId}`)
      .then((r) => {
        const d = r.data || {};
        setForm({
          customer_name: d.customer_name || '', email: d.email || '', phone: d.phone || '',
          event_date: d.event_date || '', event_time: d.event_time || '', persons: d.persons ?? '',
        });
        setItems((d.items && d.items.length) ? d.items.map((i) => ({ description: i.description || '', quantity: i.quantity ?? 1, unit_price: i.unit_price ?? '' })) : [{ ...EMPTY_ITEM }]);
        setNote(d.note || '');
      })
      .catch(() => toast.error('Angebot konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, offerId]);

  const total = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateItem = (idx, field, val) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  const addItem = () => setItems((arr) => [...arr, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems((arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  const submit = async (send) => {
    if (!form.customer_name.trim()) { toast.error('Bitte einen Namen angeben.'); return; }
    const cleanItems = items.filter((i) => (i.description || '').trim())
      .map((i) => ({ description: i.description.trim(), quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 }));
    if (!cleanItems.length) { toast.error('Bitte mindestens eine Position mit Beschreibung angeben.'); return; }
    if (send && !form.email.trim()) { toast.error('Bitte eine E-Mail-Adresse für den Versand angeben.'); return; }
    setBusy(true);
    try {
      await api.post('/standalone-offers', {
        offer_id: offerId || null,
        customer_name: form.customer_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        event_date: form.event_date || null,
        event_time: form.event_time.trim() || null,
        persons: form.persons === '' ? null : Number(form.persons),
        items: cleanItems,
        note,
        send,
      });
      toast.success(send ? 'Angebot gesendet' : 'Angebot als Entwurf gespeichert');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="standalone-offer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <FileText className="w-5 h-5" /> {offerId ? 'Angebot bearbeiten' : 'Neues Angebot'} (ohne Reservierung)
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-[#9CA3AF]">Wird geladen…</div>
        ) : (
          <div className="space-y-4">
            {/* Gastdaten */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Name *</Label>
                <Input value={form.customer_name} onChange={(e) => setField('customer_name', e.target.value)} placeholder="Gast / Firma" className="mt-1" data-testid="so-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">E-Mail</Label>
                <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="gast@example.com" className="mt-1" data-testid="so-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Telefon</Label>
                <Input value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="+49 …" className="mt-1" data-testid="so-phone" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Wunschdatum</Label>
                <Input type="date" value={form.event_date} onChange={(e) => setField('event_date', e.target.value)} className="mt-1" data-testid="so-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Uhrzeit</Label>
                <Input value={form.event_time} onChange={(e) => setField('event_time', e.target.value)} placeholder="z. B. 18:00" className="mt-1" data-testid="so-time" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Personen</Label>
                <Input type="number" min="0" value={form.persons} onChange={(e) => setField('persons', e.target.value)} placeholder="z. B. 20" className="mt-1" data-testid="so-persons" />
              </div>
            </div>

            {/* Positionen */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Positionen</Label>
              <div className="mt-2 mb-2">
                <OfferTemplateBar items={items} note={note} onApply={(newItems, newNote) => { setItems(newItems); if (newNote) setNote(newNote); }} />
              </div>
              <div className="space-y-2 mt-2" data-testid="so-items">
                {items.map((it, idx) => (
                  <div key={idx} className="flex items-start gap-2" data-testid={`so-item-${idx}`}>
                    <Input value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Beschreibung" className="flex-1" data-testid={`so-item-desc-${idx}`} />
                    <Input type="number" min="0" step="1" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} placeholder="Menge" className="w-16 text-center" data-testid={`so-item-qty-${idx}`} />
                    <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} placeholder="€" className="w-24 text-right" data-testid={`so-item-price-${idx}`} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="text-[#EF4444] shrink-0" data-testid={`so-item-remove-${idx}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2" data-testid="so-add-item">
                <Plus className="w-4 h-4 mr-1" /> Position hinzufügen
              </Button>
            </div>

            <div className="flex items-center justify-between border-t border-[#E5E5E0] pt-3">
              <span className="text-sm font-medium text-[#5C5C59]">Gesamt</span>
              <span className="text-lg font-semibold text-[#1C1C19]" data-testid="so-total">{total.toFixed(2).replace('.', ',')} €</span>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Anschreiben / Hinweis (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Persönliche Nachricht, Anzahlung, Details …" rows={3} className="mt-1" data-testid="so-note" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="so-cancel">Abbrechen</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={busy || loading} data-testid="so-save-draft">Entwurf speichern</Button>
          <Button onClick={() => submit(true)} disabled={busy || loading} className="bg-[#1C1C19] hover:bg-[#3A3A35]" data-testid="so-send">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Angebot senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
